<template>
  <div :id="modalId" ref="root" tabindex="-1" role="dialog" class="modal fade">
    <div class="modal-dialog" :class="modalSizeClass" role="document">
      <div class="modal-content">
        <div v-if="!headerless" class="modal-header">
          <slot name="header">
            <h4 v-if="title" class="modal-title mt-0">
              <slot name="title">
                {{ title }}
              </slot>
            </h4>
            <button type="button" class="btn-close" :aria-label="i18n.t('close')" @click="close" />
          </slot>
        </div>
        <div class="sub-heading">
          <slot name="sub-heading" />
        </div>
        <div class="modal-body" :class="{'paddingless-body': paddinglessBody}">
          <slot name="body" />
        </div>
        <div v-if="hasFooterSlot" class="modal-footer">
          <slot name="footer" />
        </div>
      </div>
    </div>

    <slot name="outer-navigation" />
  </div>
</template>

<script>
import { Modal } from 'bootstrap'

export default {
  props: {
    modalId: {
      type: String,
      required: true
    },
    title: {
      type: String,
      required: false
    },
    showOnMount: {
      type: Boolean,
      required: false,
      default: false
    },
    size: {
      type: String,
      required: false,
      default: 'lg'
    },
    clickOutsideToClose: {
      type: Boolean,
      required: false,
      default: true
    },
    headerless: {
      type: Boolean,
      required: false,
      default: false
    },
    paddinglessBody: {
      type: Boolean,
      required: false,
      default: false
    }
  },

  emits: ['closed', 'opened'],

  data() {
    return {
      bootstrapModal: null,
      transitionInProgress: false,
      visibility: 'hidden'
    }
  },

  computed: {
    backdropSetting() {
      if (this.clickOutsideToClose) {
        return true
      } else {
        return 'static'
      }
    },

    hasFooterSlot() {
      return Object.keys(this.$slots).includes('footer')
    },

    modalSizeClass() {
      return `modal-${this.size}`
    }
  },

  watch: {
    clickOutsideToClose: function() {
      // Update the settings on the live Bootstrap modal to change whether clicks on the backdrop close the modal.
      // This relies on an undocumented internal data structure, and will need to be updated at our next major version
      // upgrade of bootstrap. Unfortunately, there is no supported API for changing this attribute of an already-open
      // modal.
      this.bootstrapModal._config.backdrop = this.backdropSetting
    }
  },

  mounted() {
    const vm = this
    const rootElement = this.$refs.root

    rootElement.addEventListener('hidden.bs.modal', function () {
      vm.transitionInProgress = false
      vm.visibility = 'hidden'
      vm.$emit('closed')
    })

    rootElement.addEventListener('shown.bs.modal', function () {
      vm.transitionInProgress = false
      vm.visibility = 'shown'
      vm.$emit('opened')
    })

    this.bootstrapModal = Modal.getOrCreateInstance(this.$refs.root, {backdrop: this.backdropSetting})
    if (this.showOnMount) {
      this.open()
    }
  },

  beforeUnmount() {
    if (this.visibility === 'shown') {
      // When a bootstrap modal opens, it adds `overflow: hidden` and `padding-right: 0px` to the body.
      // Closing the modal normally removes those, but if we open another modal right away, a timing issue
      // can prevent these styles from clearing when we close the second modal; this then
      // prevents the page from scrolling. To avoid this issue, clear the body style ourselves.
      document.body.attributeStyleMap.delete('overflow')
      document.body.attributeStyleMap.delete('padding-right')
    }

    // If we call dispose() while there is a hide-modal transition in progress, then the callback at the end of the
    // transition will not be able to complete properly, causing a JS console error.
    if (!this.transitionInProgress) {
      this.bootstrapModal.dispose()
    }
  },

  methods: {
    close() {
      this.transitionInProgress = true
      this.bootstrapModal.hide()
    },

    open() {
      this.transitionInProgress = true
      this.bootstrapModal.show()
    }
  }

}
</script>

<style lang="scss" scoped>
  .paddingless-body {
    padding-left: 0;
    padding-right: 0;
  }
</style>

